import { ApiData, StationCoordinateItem, MarketPriceItem, TruckingRateItem } from '../../../../contexts/apiService'

interface AnalysisInput {
    rows: DataRow[];
    selectedCarrier: string;
    purchaseDeduct: number;
    apiData: ApiData;
    specificMarket?: string;
    saleDeduct?: number;
}

interface RouteData {
    leaseName: string;
    coordinates: { lat: number; lng: number };
    closestStations: Array<{
        name: string;
        lat: number;
        lng: number;
        haversineDistance: number;
        actualDistance: number;
        actualDuration: string;
        routeStatus: string;
        discountInfo: MarketPriceItem;
        displayName: string;
    }>;
}

interface DistanceCalculationResult {
    routeData: RouteData[];
    errors: string[];
}

interface DataRow {
    leaseName: string;
    coordinates: string;
    estGravity: string;
    bpd: string;
    bestMarket?: string;
    secBestMarket?: string;
}

interface GoogleMapsRouteResult {
    distance: number;
    duration: string;
    status: string;
}

interface ClosestStation {
    name: string;
    lat: number;
    lng: number;
    haversineDistance: number;
    discountInfo: MarketPriceItem;
    displayName: string;
}

export interface AnalysisResult {
    leaseName: string;
    coordinates: string;
    trucksPerMonth: number;
    error: boolean;
    
    // All ranked options (up to 5)
    allOptions: Array<{
        closestMarket: string;
        distance: number;
        duration: string;
        displayName: string;
        truckingRate: number | null;
        marketDiscount: number | null;
        netValue: number | null;
        margin: number | null;
    }>;
}

interface AnalysisOutput {
    analysisResults: AnalysisResult[];
    errors: string[];
}

interface FilteredMarket extends StationCoordinateItem {
    discountInfo: MarketPriceItem;
}

/**
 * STEP 1A: Parse coordinates from various string formats
 * Supports formats like: "lat, lng", "lat,lng", "lat lng"
 * Returns null for invalid formats
*/
const parseCoordinates = (coordString: string) => {
    if (!coordString || typeof coordString !== 'string') return null;
    
    // Remove whitespace
    const cleaned = coordString.trim();
    
    // Try different coordinate formats
    // Format: "lat, lng" or "lat,lng"
    const commaMatch = cleaned.match(/^(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)$/);
    if (commaMatch) {
        return {
            lat: parseFloat(commaMatch[1]),
            lng: parseFloat(commaMatch[2])
        };
    }
    
    // Format: "lat lng" (space separated)
    const spaceMatch = cleaned.match(/^(-?\d+\.?\d*)\s+(-?\d+\.?\d*)$/);
    if (spaceMatch) {
        return {
            lat: parseFloat(spaceMatch[1]),
            lng: parseFloat(spaceMatch[2])
        };
    }
    
    return null;
};

/**
    * STEP 1B: Validate if coordinates are within reasonable bounds
    * Latitude: -90 to 90, Longitude: -180 to 180
*/
// Validate if coordinates are within reasonable bounds
const isValidCoordinate = (lat: number, lng: number) => {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

/**
 * STEP 2A: Calculate straight-line distance using Haversine formula
 * Used for initial filtering to find closest stations
 * Returns distance in miles
 */
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 3959; // Earth's radius in miles (use 6371 for kilometers)
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

/**
 * STEP 2B: Get actual trucking route using Google Maps API
 * This provides real driving distances and durations (more accurate than Haversine)
 */
// Google Maps API function for calculating trucking routes
const calculateTruckingRoute = async (
    origin: { lat: number; lng: number }, 
    destination: { lat: number; lng: number }
): Promise<GoogleMapsRouteResult> => {
    // Check if Google Maps is loaded
    if (!window.google || !window.google.maps) {
        throw new Error('Google Maps not loaded');
    }

    return new Promise((resolve, reject) => {
        const directionsService = new window.google.maps.DirectionsService();
        
        directionsService.route({
            origin: new window.google.maps.LatLng(origin.lat, origin.lng),
            destination: new window.google.maps.LatLng(destination.lat, destination.lng),
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL
        }, (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK && result) {
                const route = result.routes[0];
                const leg = route.legs[0];
                
                if (!leg.distance || !leg.duration) {
                    reject(new Error('Invalid route data: missing distance or duration'));
                    return;
                }

                const distanceInMiles = leg.distance.value * 0.000621371;
                
                resolve({
                    distance: distanceInMiles,
                    duration: leg.duration.text,
                    status: 'OK'
                });
            } else {
                reject(new Error(`Google Maps Directions API error: ${status}`));
            }
        });
    });
};

/**
 * STEP 3A: Filter and combine market coordinates with discount information
 * Only keeps markets that have both coordinate data AND discount pricing
 * This ensures we can calculate both routes and profitability
 */
const getFilteredMarkets = (apiData: ApiData): FilteredMarket[] => {
    if (!apiData?.market_coordinates || !apiData?.market_discounts) {
        return [];
    }

    // Filter coordinates that have matching discount entries
    const filteredMarkets = apiData.market_coordinates.filter((coordinate: StationCoordinateItem) => {
        const matchingDiscount = apiData.market_discounts!.find((discount: MarketPriceItem) => 
            discount.station_owner === coordinate.market && 
            discount.station === coordinate.station
        );
        return matchingDiscount !== undefined;
    });

    // Combine coordinate and discount data for easy access
    return filteredMarkets.map((coordinate: StationCoordinateItem) => {
        const matchingDiscount = apiData.market_discounts!.find((discount: MarketPriceItem) => 
            discount.station_owner === coordinate.market && 
            discount.station === coordinate.station
        )!;
        
        return {
            ...coordinate,
            discountInfo: matchingDiscount
        };
    });
};

/**
 * STEP 4A: Calculate routes and distances for all input rows
 * This is the core analysis function that:
 * 1. Validates input data
 * 2. Finds closest stations using Haversine formula
 * 3. Gets actual driving routes using Google Maps
 * 4. Handles errors gracefully
 */
// Calculate distances for all valid rows
const calculateDistancesForAllRows = async (
    rows: DataRow[],
    apiData: ApiData,
    specificMarket?: string,
): Promise<DistanceCalculationResult> => {

    // Get markets with both coordinates and pricing data
    const filteredMarkets = getFilteredMarkets(apiData);
    
    if (filteredMarkets.length === 0) {
        throw new Error('No market coordinates with matching discounts available. Please ensure market data is loaded and has matching discount entries.');
    }

    // If specific market is selected, filter to only that market
    let marketsToAnalyze = filteredMarkets;
    if (specificMarket) {
        marketsToAnalyze = filteredMarkets.filter(market => 
            market.station === specificMarket || 
            market.discountInfo.station === specificMarket
        );
        
        if (marketsToAnalyze.length === 0) {
            throw new Error(`Selected market "${specificMarket}" not found in available markets.`);
        }
    }

    // Filter rows that have valid data
    const validRows = rows.filter((row: DataRow) => {
        const hasLeaseName = row.leaseName.trim() !== '';
        const hasCoordinates = row.coordinates.trim() !== '';
        return hasLeaseName && hasCoordinates;
    });

    if (validRows.length === 0) {
        throw new Error('Please add at least one row with lease name and coordinates before analyzing routes.');
    }

    const routeData: RouteData[] = [];
    const errors: string[] = [];

    // Process each lease location sequentially to avoid API rate limits
    for (let rowIndex = 0; rowIndex < validRows.length; rowIndex++) {
        const row = validRows[rowIndex];
        const coords = parseCoordinates(row.coordinates);
        
        // Validate coordinate format
        if (!coords) {
            errors.push(`Row ${rowIndex + 1}: Invalid coordinate format "${row.coordinates}"`);
            continue;
        }

        // Validate coordinate bounds
        if (!isValidCoordinate(coords.lat, coords.lng)) {
            errors.push(`Row ${rowIndex + 1}: Coordinates out of valid range`);
            continue;
        }

        try {
            if (specificMarket) {
                // FAST PATH: Only analyze the specific market (no distance sorting needed)
                const targetMarket = marketsToAnalyze[0]; // Should only be one market
                
                try {
                    const routeResult = await calculateTruckingRoute(coords, {
                        lat: targetMarket.latitude,
                        lng: targetMarket.longitude
                    });
                    
                    const stationResult = {
                        name: targetMarket.station,
                        lat: targetMarket.latitude,
                        lng: targetMarket.longitude,
                        haversineDistance: calculateDistance(coords.lat, coords.lng, targetMarket.latitude, targetMarket.longitude),
                        actualDistance: routeResult.distance,
                        actualDuration: routeResult.duration,
                        routeStatus: routeResult.status,
                        discountInfo: targetMarket.discountInfo,
                        displayName: `${targetMarket.discountInfo.station} - ${targetMarket.discountInfo.market}`
                    };

                    routeData.push({
                        leaseName: row.leaseName,
                        coordinates: coords,
                        closestStations: [stationResult] // Only one station
                    });
                    
                } catch (error) {
                    console.error(`Error calculating route to ${targetMarket.station}:`, error);
                    routeData.push({
                        leaseName: row.leaseName,
                        coordinates: coords,
                        closestStations: [{
                            name: targetMarket.station,
                            lat: targetMarket.latitude,
                            lng: targetMarket.longitude,
                            haversineDistance: calculateDistance(coords.lat, coords.lng, targetMarket.latitude, targetMarket.longitude),
                            actualDistance: 0,
                            actualDuration: 'Error',
                            routeStatus: 'ERROR',
                            discountInfo: targetMarket.discountInfo,
                            displayName: `${targetMarket.discountInfo.station} - ${targetMarket.discountInfo.market}`
                        }]
                    });
                }
            } else {
                // STEP 4A-1: Find closest stations using straight-line distance (fast)
                const stationsWithDistance = filteredMarkets.map((market: FilteredMarket) => {
                    const marketLat = market.latitude;
                    const marketLng = market.longitude;
                    const marketName = market.station;

                    if (marketLat && marketLng) {
                        const haversineDistance = calculateDistance(coords.lat, coords.lng, marketLat, marketLng);
                        return {
                            name: marketName,
                            lat: marketLat,
                            lng: marketLng,
                            haversineDistance,
                            discountInfo: market.discountInfo,
                            displayName: `${market.discountInfo.station} - ${market.discountInfo.market}`
                        };
                    }
                    return null;
                }).filter((station): station is ClosestStation => station !== null);

                // STEP 4A-2: Sort by distance and take top 5 closest stations
                const closest5Stations = stationsWithDistance
                    .sort((a, b) => a.haversineDistance - b.haversineDistance)
                    .slice(0, 5);

                if (closest5Stations.length === 0) {
                    errors.push(`Row ${rowIndex + 1}: No valid stations found`);
                    continue;
                }

                // STEP 4A-3: Get actual driving routes for the 5 closest stations
                const googleMapsResults = await Promise.all(
                    closest5Stations.map(async (station: ClosestStation) => {
                        try {
                            const routeResult = await calculateTruckingRoute(coords, {
                                lat: station.lat,
                                lng: station.lng
                            });
                            
                            return {
                                ...station,
                                actualDistance: routeResult.distance,
                                actualDuration: routeResult.duration,
                                routeStatus: routeResult.status
                            };
                        } catch (error) {
                            console.error(`Error calculating route to ${station.name}:`, error);
                            return {
                                ...station,
                                actualDistance: 0,
                                actualDuration: 'Error',
                                routeStatus: 'ERROR'
                            };
                        }
                    })
                );

                // Store the complete route data for this lease
                routeData.push({
                    leaseName: row.leaseName,
                    coordinates: coords,
                    closestStations: googleMapsResults
                });

            }
            // Add a small delay to avoid hitting API rate limits
            if (!specificMarket) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }

        } catch (error) {
            console.error(`Error processing row ${rowIndex + 1}:`, error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            errors.push(`Row ${rowIndex + 1}: Error during analysis - ${errorMessage}`);
        }
    }

    return { routeData, errors };
};

/**
 * STEP 5A: Get trucking rate based on distance and carrier
 * Uses a tiered rate structure where rates apply to distance brackets
 * Returns the rate per mile for the selected carrier
 */
const getTruckingRate = (distance: number, carrierName: string, apiData: ApiData): number | null => {
    if (!apiData?.trucking_rates || !carrierName) {
        return null;
    }

    // Sort trucking rates by mileage to find the correct bracket
    const sortedRates = [...apiData.trucking_rates].sort((a: TruckingRateItem, b: TruckingRateItem) => {
        const aMileage = parseInt(a.mileage);
        const bMileage = parseInt(b.mileage);
        return aMileage - bMileage;
    });
    
    // Find the rate bracket that applies to this distance
    let applicableRate: TruckingRateItem | null = null;
    
    for (const rate of sortedRates) {
        const rateMileage = parseInt(rate.mileage);
        if (distance >= rateMileage) {
            applicableRate = rate;
        } else {
            break;
        }
    }
    
    // If no rate found, use the lowest bracket
    if (!applicableRate && sortedRates.length > 0) {
        applicableRate = sortedRates[0];
    }
    
    if (!applicableRate) {
        return null;
    }
    
    const carrierRate = (applicableRate as any)[carrierName];
    if (!carrierRate || typeof carrierRate !== 'string') {
        return null;
    }
    
    return parseFloat(carrierRate);
};

/**
 * STEP 5B: Get market discount/premium for a specific station and market
 * Only uses entries marked as 'main' (main === 1)
 * Returns the discount amount (can be negative for premiums)
 */
const getMarketDiscount = (stationName: string, marketName: string, apiData: ApiData): number | null => {
    if (!apiData?.market_discounts) {
        return null;
    }

    const discountEntry = apiData.market_discounts.find((discount: MarketPriceItem) => 
        discount.station === stationName && 
        discount.market === marketName && 
        discount.main === 1
    );

    if (!discountEntry || !discountEntry.discount) {
        return null;
    }

    return parseFloat(discountEntry.discount);
};

/**
 * STEP 5C: Calculate trucks needed per month based on barrels per day (BPD)
 * Assumes: 30 days per month, 170 barrels per truck load
 * Returns 0 if less than 1 truck needed per month
 */
const calculateTrucksPerMonth = (bpd: string): number => {
    if (!bpd || bpd.trim() === '') {
        return 0;
    }
    
    const bpdValue = parseFloat(bpd);
    if (isNaN(bpdValue) || bpdValue <= 0) {
        return 0;
    }
    
    // Calculate monthly barrels: BPD * 30 days
    const monthlyBarrels = bpdValue * 30;
    
    // Calculate trucks needed: monthly barrels / 170 (avg load per truck)
    const trucksNeeded = monthlyBarrels / 170;
    
    // If less than 1 truck needed, return 0
    if (trucksNeeded < 1) {
        return 0;
    }
    
    return Math.round(trucksNeeded);
};

/**
 * STEP 6A: Calculate final financial results and find best routes
 * This function:
 * 1. Calculates trucking costs and market discounts for each route
 * 2. Computes net values and margins
 * 3. Sorts routes by profitability
 * 4. Returns the best and second-best options
 */
const calculateFinalResults = (
    routeData: RouteData[], 
    selectedCarrier: string,
    rows: DataRow[],
    purchaseDeduct: number = 0,
    apiData: ApiData,
    maxResults: number = 5,
    saleDeduct?: number,
    specificMarket?: string,
): AnalysisResult[] => {
    return routeData.map((data: RouteData) => {
        // Find the corresponding row to get BPD data
        const correspondingRow = rows.find((row: DataRow) => 
            row.leaseName === data.leaseName && 
            parseCoordinates(row.coordinates)?.lat === data.coordinates.lat &&
            parseCoordinates(row.coordinates)?.lng === data.coordinates.lng
        );
        
        const trucksPerMonth = correspondingRow ? calculateTrucksPerMonth(correspondingRow.bpd) : 0;

        // Find valid routes (non-error ones)
        const validRoutes = data.closestStations.filter(result => result.routeStatus !== 'ERROR');
        
        if (validRoutes.length === 0) {
            // No valid routes - return error case
            return {
                leaseName: data.leaseName,
                coordinates: `${data.coordinates.lat}, ${data.coordinates.lng}`,
                trucksPerMonth,
                error: true,
                allOptions: []
            };
        }
        
        const routesWithCalculations = validRoutes.map(route => {
            const rate = getTruckingRate(route.actualDistance, selectedCarrier, apiData);
            
            let marketDiscount = null;
            
            // STEP 6A-1: Determine market discount source
            if (specificMarket && saleDeduct !== undefined) {
                // FAST PATH: Use user-provided sale deduct (skip market discount lookup)
                marketDiscount = saleDeduct;
                console.log(`Using user-provided sale deduct: $${saleDeduct} for ${route.displayName}`);
            } else {
                // NORMAL PATH: Look up market discount from API data
                marketDiscount = route.discountInfo ? 
                    getMarketDiscount(route.discountInfo.station, route.discountInfo.market, apiData) : null;
            }
            
            let netValue = null;
            let margin = null;

            if (rate !== null && marketDiscount !== null) {
                netValue = -rate + marketDiscount;
                if (purchaseDeduct !== 0) {
                    margin = netValue - purchaseDeduct;
                }
            }
            
            return {
                ...route,
                truckingRate: rate,
                marketDiscount,
                netValue,
                margin,
            };
        }).filter(route => route.netValue !== null);

        // Sort routes by highest net value (best economics)
        const sortedRoutes = routesWithCalculations.sort((a, b) => {
            if (a.netValue === null && b.netValue === null) return 0;
            if (a.netValue === null) return 1;
            if (b.netValue === null) return -1;
            return b.netValue - a.netValue;
        });

        // Get top N results and format them
        const allOptions = sortedRoutes
            .slice(0, Math.min(maxResults, sortedRoutes.length))
            .map(route => ({
                closestMarket: route.name,
                distance: Math.round(route.actualDistance * 100) / 100,
                duration: route.actualDuration,
                displayName: route.displayName,
                truckingRate: route.truckingRate,
                marketDiscount: route.marketDiscount,
                netValue: route.netValue,
                margin: route.margin,
            }));

        return {
            leaseName: data.leaseName,
            coordinates: `${data.coordinates.lat}, ${data.coordinates.lng}`,
            trucksPerMonth,
            error: false,
            allOptions
        };
    });
};

// Main analysis function - this is what gets called when the button is clicked
export const runRouteAnalysis = async (input: AnalysisInput): Promise<AnalysisOutput> => {
    try {
        // Step 1: Get distance data (this is the expensive operation with Google Maps)
        const { routeData, errors } = await calculateDistancesForAllRows(
            input.rows,
            input.apiData,
            input.specificMarket,
        );
        
        // Step 2: Calculate final results with pricing and financial analysis
        const analysisResults = calculateFinalResults(
            routeData, 
            input.selectedCarrier, 
            input.rows, 
            input.purchaseDeduct,
            input.apiData,
            5, // maxResults
            input.saleDeduct,
            input.specificMarket,
        );
        
        return { analysisResults, errors };
        
    } catch (error) {
        console.error('Analysis error:', error);
        throw error;
    }
};